import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { ToastProvider } from '@locus-taxy/encore-ui-toolkit';
import {
  I18nProvider,
  i18n as toolkitI18n,
  t as toolkitTranslate,
} from '@locus-taxy/encore-i18n-toolkit';
import {
  DiscoProvider,
  configureDiscoLocale,
  configureDiscoTranslate,
} from '@locus-taxy/disco-ui';
import { NumberUtils } from '@locus-taxy/encore-utils';
import { IdentityProvider } from './auth';
import App from './App';
import { NetworkProvider } from './api/network/NetworkProvider';
import { UserProvider } from './contexts/UserContext';
import SuspenseLoader from './components/SuspenseLoader';

import { ErrorBoundary } from './components/ErrorBoundary';
import { useClientId } from './hooks/useClientId';
import { GridEntityPickerProvider } from '@locus-taxy/encore-entity-picker-grid';
import { EntityDetailFrameworkProvider } from './components/EntityDetailFrameworkProvider';
import { Agentation } from 'agentation';
import { AUTH0_AUDIENCE, AUTH0_CLIENT_ID, AUTH0_DOMAIN } from './config';

import { initBrowserO11y } from './utils/o11yUtils';
import { initBugsnag } from './utils/bugsnag';
import { initModuleFederationCacheBypass } from './utils/moduleFederationCache';

import '@locus-taxy/disco-map/map.css';
import './styles/tailwind.css';
import './styles/disco-mode.css';

// Wrapper component to handle Auth0 redirect callback with React Router
export function AppWrapper() {
  const navigate = useNavigate();
  const resolvedClientId = useClientId();

  return (
    <DiscoProvider>
      <GridEntityPickerProvider>
        <I18nProvider>
          <ToastProvider>
            <ErrorBoundary>
              <IdentityProvider
                domain={AUTH0_DOMAIN}
                clientId={AUTH0_CLIENT_ID}
                authorizationParams={{
                  redirect_uri: window.location.origin,
                  audience: AUTH0_AUDIENCE,
                  l_client_id: resolvedClientId,
                }}
                cacheLocation="localstorage"
                useRefreshTokens
                useRefreshTokensFallback
                onRedirectCallback={appState => {
                  const requestedPath =
                    appState?.returnTo ?? window.location.pathname;
                  navigate(requestedPath, { replace: true });
                }}
              >
                <UserProvider>
                  <NetworkProvider>
                    {/* Inside NetworkProvider so the framework shares the
                        network layer's react-query cache and the resolved
                        user context. Precedent: GridEntityPickerProvider. */}
                    <EntityDetailFrameworkProvider>
                      <Suspense fallback={<SuspenseLoader />}>
                        <App />
                      </Suspense>
                    </EntityDetailFrameworkProvider>
                  </NetworkProvider>
                </UserProvider>
              </IdentityProvider>
            </ErrorBoundary>
          </ToastProvider>
        </I18nProvider>
      </GridEntityPickerProvider>
      {/* Visual feedback toolbar — vite dev server only. import.meta.env.DEV
          is compile-time false in every `vite build` (Amplify previews
          included), so the component and its global style overrides (e.g.
          `svg[fill="none"]{fill:none!important}` which broke Rating star
          fill) tree-shake away from every deployed bundle. */}
      {import.meta.env.DEV && <Agentation />}
    </DiscoProvider>
  );
}

// Wire disco-ui's safe-default t() shim to the toolkit's translate so disco-ui
// components resolve catalog translations instead of their inline English
// defaults (disco-ui otherwise only best-effort auto-resolves the peer async).
configureDiscoTranslate(toolkitTranslate);

// Same deterministic-wiring rationale as configureDiscoTranslate above, for the
// active *locale* rather than the translate function: a reader beats disco-ui's
// best-effort async auto-resolve. NumberUtils has no reader concept (its locale
// is a plain field, consumed by grid/detail-view currency formatting), so it
// also needs an explicit re-configure on every language switch, not just once
// at boot.
configureDiscoLocale(() => toolkitI18n.language);
NumberUtils.getInstance().configure({ locale: toolkitI18n.language });
toolkitI18n.on('languageChanged', language => {
  NumberUtils.getInstance().configure({ locale: language });
});

initBugsnag();
initBrowserO11y();
initModuleFederationCacheBypass();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BrowserRouter>
      <AppWrapper />
    </BrowserRouter>
  </StrictMode>
);
